import { motion } from 'framer-motion';
import { Check, Zap, Crown, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const plans = [
  {
    name: 'Por Uso',
    icon: Zap,
    price: '8,90',
    period: 'por utilização',
    description: 'Ideal para quem recebe encomendas ocasionalmente.',
    features: [
      'Cadastro gratuito',
      'Pague apenas quando usar',
      'Acesso a todos os lockers',
      'Notificações em tempo real',
      'Suporte via chat',
      'Sem fidelidade',
    ],
    cta: 'Começar Grátis',
    highlighted: false,
  },
  {
    name: 'Assinatura',
    icon: Crown,
    price: '59,90',
    period: '/mês',
    description: 'Perfeito para quem compra online frequentemente.',
    features: [
      'Uso ilimitado dos lockers',
      'Prioridade na escolha do locker',
      'Notificações em tempo real',
      'Suporte prioritário 24/7',
      'Histórico completo de entregas',
      'Cancele quando quiser',
    ],
    cta: 'Assinar Agora',
    highlighted: true,
    badge: 'Mais Popular',
  },
];

const faqs = [
  {
    question: 'Como funciona o plano por uso?',
    answer: 'No plano por uso, você paga apenas R$ 8,90 cada vez que utilizar um locker para receber sua encomenda. O cadastro é gratuito e não há mensalidade.',
  },
  {
    question: 'Posso cancelar a assinatura a qualquer momento?',
    answer: 'Sim! Você pode cancelar sua assinatura a qualquer momento, sem multas ou taxas de cancelamento. O acesso continua até o fim do período já pago.',
  },
  {
    question: 'Por quanto tempo minha encomenda pode ficar no locker?',
    answer: 'Sua encomenda pode permanecer no locker por até 72 horas após a entrega. Após esse período, pode haver cobrança adicional de armazenamento.',
  },
  {
    question: 'Quais formas de pagamento são aceitas?',
    answer: 'Aceitamos cartões de crédito, débito, Pix e boleto bancário. Para assinaturas, a cobrança é feita automaticamente no cartão cadastrado.',
  },
  {
    question: 'E se eu não estiver perto de um locker?',
    answer: 'Estamos em constante expansão! Você pode verificar os lockers disponíveis no mapa ou solicitar a instalação de um locker na sua região.',
  },
];

export const PlanosPage = () => {
  return (
    <main className="pt-20" data-testid="planos-page">
      {/* Hero */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block bg-lokker-lime/20 text-slate-700 text-sm font-medium px-4 py-2 rounded-sm mb-4">
              Planos para Pessoa Física
            </span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 mb-4">
              Escolha o plano ideal para você
            </h1>
            <p className="text-lg text-slate-600">
              Flexibilidade para se adequar ao seu estilo de vida. Sem surpresas, sem taxas escondidas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`plan-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`relative bg-white rounded-sm overflow-hidden ${
                  plan.highlighted
                    ? 'border-2 border-lokker-blue shadow-xl'
                    : 'border border-slate-200 shadow-sm'
                }`}
              >
                {plan.badge && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-lokker-blue text-white text-xs font-bold px-4 py-1 uppercase tracking-wider">
                      {plan.badge}
                    </div>
                  </div>
                )}

                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                      plan.highlighted ? 'bg-lokker-blue' : 'bg-slate-100'
                    }`}>
                      <plan.icon className={`w-6 h-6 ${
                        plan.highlighted ? 'text-white' : 'text-lokker-blue'
                      }`} />
                    </div>
                    <h3 className="font-heading font-bold text-2xl text-slate-900">
                      {plan.name}
                    </h3>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm text-slate-500">R$</span>
                      <span className="font-heading font-bold text-5xl text-slate-900">
                        {plan.price}
                      </span>
                      <span className="text-slate-500">{plan.period}</span>
                    </div>
                  </div>

                  <p className="text-slate-600 mb-6">{plan.description}</p>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-sm flex items-center justify-center ${
                          plan.highlighted ? 'bg-lokker-lime' : 'bg-slate-100'
                        }`}>
                          <Check className={`w-3 h-3 ${
                            plan.highlighted ? 'text-black' : 'text-lokker-blue'
                          }`} />
                        </div>
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    data-testid={`plan-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full py-6 text-base rounded-sm ${
                      plan.highlighted
                        ? 'btn-primary'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <div className="flex flex-wrap justify-center gap-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Pagamento seguro</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Cancele quando quiser</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                <span>Suporte em português</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Compare os planos
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-sm overflow-hidden max-w-3xl mx-auto"
          >
            <table className="w-full" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-4 font-heading font-semibold text-slate-900">Recurso</th>
                  <th className="text-center p-4 font-heading font-semibold text-slate-900">Por Uso</th>
                  <th className="text-center p-4 font-heading font-semibold text-lokker-blue">Assinatura</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="p-4 text-slate-700">Cadastro</td>
                  <td className="p-4 text-center text-green-600">Grátis</td>
                  <td className="p-4 text-center text-green-600">Grátis</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="p-4 text-slate-700">Custo por uso</td>
                  <td className="p-4 text-center text-slate-900">R$ 8,90</td>
                  <td className="p-4 text-center text-green-600">Incluso</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="p-4 text-slate-700">Uso ilimitado</td>
                  <td className="p-4 text-center text-slate-400">—</td>
                  <td className="p-4 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="p-4 text-slate-700">Prioridade de locker</td>
                  <td className="p-4 text-center text-slate-400">—</td>
                  <td className="p-4 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="p-4 text-slate-700">Suporte prioritário</td>
                  <td className="p-4 text-center text-slate-400">—</td>
                  <td className="p-4 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="p-4 text-slate-700">Notificações</td>
                  <td className="p-4 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                  <td className="p-4 text-center"><Check className="w-5 h-5 text-green-600 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="w-14 h-14 bg-lokker-lime rounded-sm flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-7 h-7 text-black" />
            </div>
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-slate-600">
              Tire suas dúvidas sobre nossos planos
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-3xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-slate-50 border border-slate-200 rounded-sm px-6"
                >
                  <AccordionTrigger className="text-left font-heading font-semibold text-slate-900 hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default PlanosPage;
